import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { BodyPrimary } from '@liveauctioneers/hammer-ui-core/text';
import { FormEvent } from 'react';
import styled from 'styled-components';

import { RouteButton } from '@/components/RouteButton/RouteButton';
import { Catalog } from '@/types/Catalog';
import { getDeskSupportLink } from '@/utils/urls';

import ReviewPageForm from './ReviewPageForm';

type Props = {
    catalog: Catalog;
    disableSubmit: boolean;
    handleContinueClick: () => void;
    handleNotTalkedToHouseClick: () => void;
    handleTalkedToHouseClick: () => void;
    note: string;
    noteChange: (event: FormEvent<HTMLTextAreaElement>) => void;
    noteValid: boolean;
    openSendSellerMessageModal: Function;
    ratingChangeAccuracy: (value: number) => void;
    ratingChangeOverall: (value: number) => void;
    ratingChangePayment: (value: number) => void;
    ratingChangeResponsiveness: (value: number) => void;
    ratingChangeShipping: (value: number) => void;
    receivedItems: boolean;
    review: string;
    reviewChange: (event: FormEvent<HTMLTextAreaElement>) => void;
    reviewError: boolean;
    reviewHeadlineChange: (event: FormEvent<HTMLInputElement>) => void;
    reviewHeadlineError: boolean;
    reviewSubmitted: boolean;
    reviewValid: boolean;
    selectedContinue: boolean;
    selectedRecievedItems: boolean;
    submitReview: () => void;
    talkedToHouse: boolean;
};

const ReviewPageBody = (props: Props) => {
    const { formatMessage } = useIntl();
    const {
        catalog,
        disableSubmit,
        handleContinueClick,
        handleNotTalkedToHouseClick,
        handleTalkedToHouseClick,
        note,
        noteChange,
        noteValid,
        openSendSellerMessageModal,
        ratingChangeAccuracy,
        ratingChangeOverall,
        ratingChangePayment,
        ratingChangeResponsiveness,
        ratingChangeShipping,
        receivedItems,
        review,
        reviewChange,
        reviewError,
        reviewHeadlineChange,
        reviewHeadlineError,
        reviewSubmitted,
        reviewValid,
        selectedContinue,
        selectedRecievedItems,
        submitReview,
        talkedToHouse,
    } = props;
    const { catalogId, sellerId, sellerName, title } = catalog;

    const showSendSellerMessageModal = () => {
        openSendSellerMessageModal(
            {
                catalogId,
                title,
            },
            sellerId,
            sellerName,
            `${title}: ${catalogId}`
        );
    };

    const displayPlea = !receivedItems && selectedRecievedItems;

    return (
        <Container>
            {displayPlea && (
                <div>
                    <MessageText>
                        <FormattedMessage id="review_page.encourage_wait" />
                    </MessageText>
                    <br />
                    <MessageText>
                        <FormattedMessage
                            id="review_page.waiting_for_items"
                            values={{
                                contactSupport: (
                                    <Link
                                        href={getDeskSupportLink()}
                                        key="review-page-contact-support-link"
                                        target="_blank"
                                    >
                                        <FormattedMessage
                                            id="review_page.contact_support"
                                            key="review-page-contact-support-link-message"
                                        />
                                    </Link>
                                ),
                                name: (
                                    <span
                                        className="hui-text-h5"
                                        key="review-page-waiting-seller-name"
                                    >
                                        {sellerName}
                                    </span>
                                ),
                                sendMessage: (
                                    <InlineButton
                                        key="review-page-send-message-button"
                                        onClick={showSendSellerMessageModal}
                                    >
                                        {formatMessage({
                                            id: 'review_page.send_message',
                                        })}
                                    </InlineButton>
                                ),
                            }}
                        />
                    </MessageText>
                    <LinkContainer>
                        <ReviewLaterButtonLink to="/dashboard/won-items">
                            {formatMessage({ id: 'review_page.later' })}
                        </ReviewLaterButtonLink>
                        <InlineButton onClick={handleContinueClick}>
                            {formatMessage({ id: 'review_page.continue' })}
                        </InlineButton>
                    </LinkContainer>
                </div>
            )}
            {selectedRecievedItems && (receivedItems || selectedContinue) && (
                <ReviewPageForm
                    disableSubmit={disableSubmit}
                    handleNotTalkedToHouseClick={handleNotTalkedToHouseClick}
                    handleTalkedToHouseClick={handleTalkedToHouseClick}
                    houseName={sellerName}
                    note={note}
                    noteChange={noteChange}
                    noteValid={noteValid}
                    ratingChangeAccuracy={ratingChangeAccuracy}
                    ratingChangeOverall={ratingChangeOverall}
                    ratingChangePayment={ratingChangePayment}
                    ratingChangeResponsiveness={ratingChangeResponsiveness}
                    ratingChangeShipping={ratingChangeShipping}
                    receivedItems={receivedItems}
                    review={review}
                    reviewChange={reviewChange}
                    reviewError={reviewError}
                    reviewHeadlineChange={reviewHeadlineChange}
                    reviewHeadlineError={reviewHeadlineError}
                    reviewSubmitted={reviewSubmitted}
                    reviewValid={reviewValid}
                    submitReview={submitReview}
                    talkedToHouse={talkedToHouse}
                />
            )}
        </Container>
    );
};

export default ReviewPageBody;

const Container = styled.div`
    margin-top: 40px;
`;

const MessageText = styled(BodyPrimary)`
    margin-top: 15px;
`;

const ReviewLaterButtonLink = styled(RouteButton)`
    margin-right: 24px;
    max-width: 264px;
`;

const LinkContainer = styled.div`
    margin: 24px 0;
    display: flex;
    flex-direction: row;
    align-items: center;
`;
